import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RegisterForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordRequirements = [
    { label: 'At least 8 characters', test: (pwd) => pwd?.length >= 8 },
    { label: 'One uppercase letter', test: (pwd) => /[A-Z]/?.test(pwd) },
    { label: 'One lowercase letter', test: (pwd) => /[a-z]/?.test(pwd) },
    { label: 'One number', test: (pwd) => /\d/?.test(pwd) },
    { label: 'One special character', test: (pwd) => /[!@#$%^&*]/?.test(pwd) }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else {
      const failedRequirements = passwordRequirements?.filter(req => !req?.test(formData?.password));
      if (failedRequirements?.length > 0) {
        newErrors.password = 'Password does not meet all requirements';
      }
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    setTimeout(() => {
      navigate('/prompt-input');
      setIsLoading(false);
    }, 2000);
  };

  const getPasswordStrength = () => {
    const metRequirements = passwordRequirements?.filter(req => req?.test(formData?.password))?.length;
    if (metRequirements === 0) return { label: '', color: '', width: '0%' };
    if (metRequirements <= 2) return { label: 'Weak', color: 'bg-error', width: '33%' };
    if (metRequirements <= 4) return { label: 'Medium', color: 'bg-warning', width: '66%' };
    return { label: 'Strong', color: 'bg-success', width: '100%' };
  };

  const strength = getPasswordStrength();

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
      <Input
        label="Full Name"
        type="text"
        name="fullName"
        placeholder="Enter your full name"
        value={formData?.fullName}
        onChange={handleChange}
        error={errors?.fullName}
        required
        disabled={isLoading}
      />
      <Input
        label="Email Address"
        type="email"
        name="email"
        placeholder="Enter your email"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
        disabled={isLoading}
      />
      <div className="relative">
        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          name="password"
          placeholder="Create a strong password"
          value={formData?.password}
          onChange={handleChange}
          error={errors?.password}
          required
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[38px] p-2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
        </button>
      </div>
      {formData?.password && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Password strength:</span>
            <span className={`font-medium ${
              strength?.label === 'Weak' ? 'text-error' :
              strength?.label === 'Medium' ? 'text-warning' :
              strength?.label === 'Strong' ? 'text-success' : ''
            }`}>
              {strength?.label}
            </span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${strength?.color}`}
              style={{ width: strength?.width }}
            />
          </div>
          <ul className="space-y-1 mt-3">
            {passwordRequirements?.map((req, index) => (
              <li
                key={index}
                className={`flex items-center gap-2 text-xs ${
                  req?.test(formData?.password) ? 'text-success' : 'text-muted-foreground'
                }`}
              >
                <Icon
                  name={req?.test(formData?.password) ? 'CheckCircle2' : 'Circle'}
                  size={14}
                />
                <span>{req?.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="relative">
        <Input
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          name="confirmPassword"
          placeholder="Re-enter your password"
          value={formData?.confirmPassword}
          onChange={handleChange}
          error={errors?.confirmPassword}
          required
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-[38px] p-2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
        >
          <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
        </button>
      </div>
      <Checkbox
        label={
          <span className="text-sm">
            I agree to the{' '}
            <button
              type="button"
              className="text-primary hover:underline font-medium"
              onClick={() => alert('Terms of Service would open here')}
            >
              Terms of Service
            </button>
            {' '}and{' '}
            <button
              type="button"
              className="text-primary hover:underline font-medium"
              onClick={() => alert('Privacy Policy would open here')}
            >
              Privacy Policy
            </button>
          </span>
        }
        name="agreeToTerms"
        checked={formData?.agreeToTerms}
        onChange={handleChange}
        error={errors?.agreeToTerms}
        required
        disabled={isLoading}
      />
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        iconName="UserPlus"
        iconPosition="right"
        className="mt-6"
      >
        {isLoading ? 'Creating account...' : 'Create Account'}
      </Button>
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">Or register with</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Button
          type="button"
          variant="outline"
          iconName="Github"
          iconPosition="left"
          onClick={() => alert('GitHub OAuth would be implemented here')}
        >
          GitHub
        </Button>
        <Button
          type="button"
          variant="outline"
          iconName="Mail"
          iconPosition="left"
          onClick={() => alert('Google OAuth would be implemented here')}
        >
          Google
        </Button>
      </div>
    </form>
  );
};

export default RegisterForm;